// ── Pass the Torch — Co-Stake & Lender-Funded Firestore Operations ─────────
// Two modes are supported:
//
//   "co_stake" (legacy default — preserves backward compat for older docs):
//     Both sides put up `amount`. Combined stake `totalWager = amount * 2`.
//     On win, payout splits 50/50. On push, both refunded. On loss, both ate.
//
//   "lender_funded" (new):
//     Lender funds the entire `totalWager`. Borrower puts up nothing — they
//     can be at 0 pts. On win, BOTH sides receive the full pickPayout (yes,
//     this is intentional bonus inflation — lending "feels rewarding" by
//     mirroring the win to both parties). On push, lender is refunded the
//     full stake, borrower gets nothing. On loss, lender eats it all and
//     borrower stays at 0.
//
// Data shape on the torchLoans doc:
//   mode                  — "co_stake" | "lender_funded" (defaults to co_stake
//                           when missing, so legacy docs still settle correctly)
//   amount                — co_stake: per-side stake; lender_funded: lender's full stake
//   borrowerContribution  — co_stake: equal to amount; lender_funded: 0
//   totalWager            — co_stake: amount × 2; lender_funded: amount (lender funds it all)
//   borrowerRefunded      — bool; true once borrower stake has been returned
//                           (always true for lender_funded since borrower never paid)
//   status                — "pending" | "active" | "won" | "lost" | "push"
//                           | "declined" | "expired"

import app from "./firebase";
import {
  getFirestore, collection, doc, addDoc, getDoc, getDocs, updateDoc,
  query, where, orderBy, limit, serverTimestamp, runTransaction,
  Timestamp,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";

const db = getFirestore(app);

// ── Constants ────────────────────────────────────────────────────────────────
export const TORCH_MAX_LOAN_DEFAULT = 100;   // per-side stake cap (co_stake) / total cap (lender_funded)
export const TORCH_MIN_LOAN = 1;             // minimum stake (was 10 — lowered so broke users can ask for any amount)
export const TORCH_MAX_REQUESTS_PER_DAY = 5;
// Note: TORCH_LENDER_MULTIPLIER is retained for legacy call sites but is no
// longer used. Left exported for backward compatibility.
export const TORCH_LENDER_MULTIPLIER = 2;

// ── Offer flow ──
// "Pass the Torch — Offer" lets a wealthy member proactively fund a broke
// teammate's pick. Lender chooses the game, side, amount, and recipient; the
// recipient sees an inbox card and can accept or decline. On accept, the pick
// auto-submits with both names on it (existing lender_funded settlement path).
// On decline, the lender is refunded.
//
// Low-balance threshold — kept for potential UI use (e.g., a "broke" badge in
// the standings list) but NO LONGER ENFORCED as an offer-recipient gate. Any
// teammate can receive a torch offer regardless of their balance.
export const TORCH_LOW_BALANCE_THRESHOLD = 50;
// Per-day cap on outgoing offers from one lender (mirror of borrower request cap).
export const TORCH_MAX_OFFERS_PER_DAY = 5;

// Loan modes. Default is "co_stake" so any legacy doc without a mode field
// continues to settle on the original 50/50 logic.
export const TORCH_MODE_CO_STAKE     = "co_stake";
export const TORCH_MODE_LENDER_FUNDED = "lender_funded";

// SERVER-OWNED-BALANCES (fix 4.1 step 2, 2026-09-28): creating, cancelling
// and expiring torch loans moves league balances, so it now runs in Cloud
// Functions (createTorchRequestCallable / createTorchOfferCallable /
// cancelTorchLoanCallable / the hourly expireTorchLoans). Same arguments and
// return shape as before ({ id, ...loan }); the server also runs the pick
// checks (odds, finished game, league sport/bet type) before any points move.
function torchCallableError(e, fallback) {
  // Surface the server's own message ("Insufficient balance — ...") as before.
  const msg = e?.message && !/^internal$/i.test(e.message) ? e.message : fallback;
  return new Error(msg);
}
async function callTorchFn(name, data, fallback) {
  try {
    const fn = httpsCallable(getFunctions(app), name);
    const res = await fn(data);
    return res.data;
  } catch (e) {
    console.error(`[Torch] ${name} failed:`, e);
    throw torchCallableError(e, fallback);
  }
}
const toIsoTime = (t) => t instanceof Date ? t.toISOString() : typeof t === "number" ? new Date(t).toISOString() : t || null;

// ── Request a Torch Loan ─────────────────────────────────────────────────────
// co_stake debits the borrower's stake now; lender_funded debits nothing.
export async function requestTorchLoan({
  borrowerUid, borrowerName,
  lenderUid, lenderName,
  leagueId, amount,
  mode = TORCH_MODE_CO_STAKE,
  gameId, gameLabel, commenceTime, sportKey,
  pickKey, pickLabel, pickOdds, pickType, pickSide, pickLine,
  pickDetails = null,
  homeTeamFull = "", awayTeamFull = "",
  message = "",
}) {
  if (!borrowerUid || !lenderUid || !leagueId || !gameId || !pickKey) throw new Error("Missing required fields");
  if (borrowerUid === lenderUid) throw new Error("You can't split a pick with yourself");
  return callTorchFn("createTorchRequestCallable", {
    borrowerName, lenderUid, lenderName, leagueId, amount, mode,
    gameId, gameLabel, commenceTime: toIsoTime(commenceTime), sportKey,
    pickKey, pickLabel, pickOdds, pickType, pickSide, pickLine: pickLine ?? null,
    pickDetails: pickDetails || null, homeTeamFull, awayTeamFull, message,
  }, "Couldn't create torch request");
}

// ── Respond to a Torch Loan (Accept / Decline) ──────────────────────────────
// Unchanged client surface — still calls the Cloud Function.
// Phase 2 will build respondToTorchCallable with co-stake logic: on accept,
// deduct lender stake, submit combined-wager pick, set status="active", link
// pickId. On decline, refund borrower stake (using admin SDK to bypass rules).
export async function respondToTorchLoan(loanId, lenderUid, accept) {
  try {
    const functions = getFunctions(app);
    const respondFn = httpsCallable(functions, "respondToTorchCallable");
    const result = await respondFn({ loanId, accept });
    return result.data;
  } catch (cfErr) {
    console.error("[Torch] respondToTorchCallable failed:", cfErr);
    const msg = cfErr?.message || "Torch response failed";
    throw new Error(msg);
  }
}

// ── Set the pickId on a loan after the pick is submitted ─────────────────────
export async function linkTorchLoanPick(loanId, pickId) {
  const loanRef = doc(db, "torchLoans", loanId);
  await updateDoc(loanRef, { pickId });
}

// ═════════════════════════════════════════════════════════════════════════════
// ═══ OFFER FLOW — Lender-Initiated Pass the Torch                          ═══
// ═════════════════════════════════════════════════════════════════════════════
// Inverse of requestTorchLoan: a wealthy member offers to fund a broke
// teammate's pick. Lender selects the game, side, and amount BEFORE inviting
// the recipient. Recipient accepts → pick submits with both names on it.
//
// Distinguished from request-flow loans by `offerInitiated: true`. Always uses
// `lender_funded` mode (borrower contributes 0). Lender's balance is debited
// AT OFFER TIME (not on accept) so the offer is real and the lender can't run
// out of points mid-flight.

// ── Offer a Torch Loan to a teammate ─────────────────────────────────────────
// The lender's stake is debited at offer time (server-side, fix 4.1 step 2).
export async function offerTorchLoan({
  lenderUid, lenderName,
  borrowerUid, borrowerName,
  leagueId, amount,
  gameId, gameLabel, commenceTime, sportKey,
  pickKey, pickLabel, pickOdds, pickType, pickSide, pickLine,
  pickDetails = null,
  homeTeamFull = "", awayTeamFull = "",
  message = "",
}) {
  if (!lenderUid || !borrowerUid || !leagueId || !gameId || !pickKey) throw new Error("Missing required fields");
  if (lenderUid === borrowerUid) throw new Error("You can't offer a torch to yourself");
  return callTorchFn("createTorchOfferCallable", {
    lenderName, borrowerUid, borrowerName, leagueId, amount,
    gameId, gameLabel, commenceTime: toIsoTime(commenceTime), sportKey,
    pickKey, pickLabel, pickOdds, pickType, pickSide, pickLine: pickLine ?? null,
    pickDetails: pickDetails || null, homeTeamFull, awayTeamFull, message,
  }, "Couldn't create torch offer");
}

// ── Respond to a Torch Offer (borrower-side accept/decline) ──────────────────
// Calls a Cloud Function that needs admin SDK — on accept it submits the pick
// using the lender's already-debited funds; on decline it refunds the lender.
export async function respondToTorchOffer(loanId, accept) {
  try {
    const functions = getFunctions(app);
    const respondFn = httpsCallable(functions, "respondToTorchOfferCallable");
    const result = await respondFn({ loanId, accept });
    return result.data;
  } catch (cfErr) {
    console.error("[Torch/Offer] respondToTorchOfferCallable failed:", cfErr);
    const msg = cfErr?.message || "Offer response failed";
    throw new Error(msg);
  }
}

// ── Pending OFFERS where I'm the borrower (recipient inbox) ──────────────────
export async function loadIncomingTorchOffers(uid, leagueId) {
  const q = query(
    collection(db, "torchLoans"),
    where("leagueId", "==", leagueId),
    where("borrowerUid", "==", uid),
    where("offerInitiated", "==", true),
    where("status", "==", "pending"),
    orderBy("createdAt", "desc"),
    limit(10),
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// ── Settle a Torch Loan ──────────────────────────────────────────────────────
// Called from the settlement flow when a pick with isTorchLoan resolves.
// Branches on `loan.mode`:
//
//   co_stake (legacy default):
//     won  → each side credited pickPayout / 2
//     push → each side refunded their `amount`
//     lost → no credits (both already lost their stake at request/accept)
//
//   lender_funded:
//     won  → BOTH sides credited the FULL pickPayout (intentional inflation —
//            the borrower's "thank you" for picking the right side, the
//            lender's reward for taking all the risk)
//     push → lender refunded `totalWager` (their full stake); borrower gets 0
//     lost → no credits (lender already ate the totalWager at accept)
//
//   pickPayout — total pick payout (totalWager + profit on won; totalWager on push; 0 on lost)
// ── settleTorchLoan (RETIRED) ────────────────────────────────────────────────
// DEAD CODE GUARD (F7). Torch-loan settlement is authoritative on the SERVER
// via settleTorchLoanAdmin in functions/index.js (called from settleAllPicks).
// This client-side copy is never imported or called, and its payout math had
// DIVERGED from the server: it credited lender_funded wins at the FULL payout to
// both sides with NO lender bonus, whereas the server pays the lender 1.5×. If
// anything ever re-wired this into the settle flow it would (a) mis-pay every
// co-stake/loan settlement and (b) race the server, writing league balances
// twice. Rather than leave ~110 lines of divergent balance-writing code sitting
// here as a landmine, the body is removed and replaced with a hard throw so any
// accidental call fails LOUDLY instead of silently mis-crediting users.
//
// The export is preserved so existing imports don't break at module load. Torch
// loan settlement must go through the Cloud Function; do not reintroduce a
// client-side settlement path.
export async function settleTorchLoan(loanId, pickStatus, pickPayout) {
  throw new Error(
    "settleTorchLoan (client) is retired — torch-loan settlement is server-side " +
    "only (settleTorchLoanAdmin in functions/index.js). Do not call this client-side."
  );
}

// ── Load a single torch loan by ID ──────────────────────────────────────────
export async function loadTorchLoanById(loanId) {
  if (!loanId) return null;
  const snap = await getDoc(doc(db, "torchLoans", loanId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

// ── Pending loans where I'm the lender (inbox) ──────────────────────────────
export async function loadIncomingTorchRequests(uid, leagueId) {
  // Lender's inbox: pending REQUESTS where I'm the lender (borrower asked me to fund).
  // Critically excludes offer-initiated loans — those flow through loadIncomingTorchOffers
  // to the RECIPIENT, not the lender. Without this filter, a lender would see their own
  // outgoing offers as if they were incoming requests (rendered as a "Take the spot" card),
  // which is nonsensical and lets the lender accept their own offer.
  const q = query(
    collection(db, "torchLoans"),
    where("leagueId", "==", leagueId),
    where("lenderUid", "==", uid),
    where("status", "==", "pending"),
    orderBy("createdAt", "desc"),
    limit(10),
  );
  const snap = await getDocs(q);
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .filter(loan => loan.offerInitiated !== true);
}

// ── Active loan where I'm the borrower ──────────────────────────────────────
export async function loadMyActiveTorchLoan(uid, leagueId) {
  // Returns the user's borrower-side loan in this league, IF they've actively
  // committed to one. We deliberately skip pending offer-initiated loans:
  // those represent offers FROM someone else that the user hasn't accepted yet,
  // and surfacing them as "my active torch loan" would (a) hide the Pass the
  // Torch CTA prematurely and (b) confuse the borrower into thinking they
  // initiated something they didn't. The recipient sees pending offers via
  // `loadIncomingTorchOffers` → inbox card instead.
  //
  // Once the recipient accepts (status flips to "active"), the offer DOES
  // show up here — at that point the borrower has committed and the standard
  // "Torch active · funded by X" strip is the right UI.
  const q = query(
    collection(db, "torchLoans"),
    where("leagueId", "==", leagueId),
    where("borrowerUid", "==", uid),
    where("status", "in", ["pending", "active"]),
    limit(5),
  );
  const snap = await getDocs(q);
  const matches = snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .filter(loan => !(loan.offerInitiated === true && loan.status === "pending"));
  return matches.length === 0 ? null : matches[0];
}

// ── Active loan where I'm the lender ────────────────────────────────────────
export async function loadMyLendingTorchLoan(uid, leagueId) {
  const q = query(
    collection(db, "torchLoans"),
    where("leagueId", "==", leagueId),
    where("lenderUid", "==", uid),
    where("status", "in", ["pending", "active"]),
    limit(5),
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// ── Recent settled loans for display in Den (last 20) ───────────────────────
export async function loadRecentTorchLoans(leagueId) {
  const q = query(
    collection(db, "torchLoans"),
    where("leagueId", "==", leagueId),
    orderBy("createdAt", "desc"),
    limit(20),
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// ── Cancel a pending Torch Loan (borrower-initiated) ────────────────────────
// Allows the borrower to withdraw a pending request before the lender
// responds. Refunds the borrower's stake (co_stake mode only — lender_funded
// borrowers never paid in) and marks the loan as "cancelled".
//
// Restricted to:
//   - borrower of the loan
//   - status === "pending"  (can't cancel an active/settled loan)
export async function cancelTorchLoan(loanId, borrowerUid) {
  if (!loanId || !borrowerUid) throw new Error("Missing required fields");
  return callTorchFn("cancelTorchLoanCallable", { loanId }, "Couldn't cancel request");
}

// ── Expire stale pending loans (6+ hours past game start) ───────────────────
// Client-triggered cleanup. Only processes loans where the CURRENT user is
// the borrower (so we have permission to refund their own member balance).
// Loans where current user is the lender of a stale request are left for the
// borrower to expire on their next session, or for the server-side cron
// (Phase 2+) to sweep.
//
// Signature changed: now requires `currentUid` so we know whose loans to refund.
export async function expireStaleLoans(leagueId, currentUid) {
  // fix 4.1 step 2: expiry runs server-side every hour (expireTorchLoans),
  // for requests AND offers -- the old client version only refunded the
  // borrower, and never refunded a lender whose offer went unanswered.
  return 0;
}

// ═════════════════════════════════════════════════════════════════════════════
// ═══ SPOT ME — Asymmetric "Bailout" Loans                                  ═══
// ═════════════════════════════════════════════════════════════════════════════
// When a user is BROKE (balance < SPOT_ME_ELIGIBILITY_MAX) they can ask a
// league mate for a one-shot bailout. Mechanic:
//
//   1. Borrower (broke, < 25 pts) requests SPOT_ME_AMOUNT (default 100) from a lender
//   2. Lender approves → 100 pts deducted from lender's balance
//   3. Borrower's NEXT pick becomes a "spot me" pick — wagered with the loaned 100
//      (NOT the borrower's own balance, which is ~0 anyway)
//   4. Settlement:
//        - WON  → lender receives 2x their stake (200 back); borrower keeps any profit beyond
//        - LOST → lender eats the 100; borrower stays at 0, no debt
//        - PUSH → lender refunded 100; borrower receives 0
//
// This is ASYMMETRIC by design — the broke borrower has no skin to put up.
// Lender takes the risk for goodwill + the 2x upside on win.
// Limited to 1 active bailout per borrower per league at a time, and 1 settled
// bailout per payday cycle (enforced server-side by checking last bailout date).

export const SPOT_ME_AMOUNT             = 100; // fixed bailout size
export const SPOT_ME_ELIGIBILITY_MAX    = 25;  // borrower must be under this to request
export const SPOT_ME_LENDER_MULTIPLIER  = 2;   // lender gets 2x back on win

// ── Request a Spot Me loan ──────────────────────────────────────────────────
// Just creates the loan doc — does NOT debit anyone yet (lender hasn't agreed).
// Borrower contributes nothing because they're broke; that's the whole point.
export async function requestSpotMe({
  borrowerUid, borrowerName,
  lenderUid, lenderName,
  leagueId,
  message = "",
}) {
  if (!borrowerUid || !lenderUid || !leagueId) throw new Error("Missing required fields");
  if (borrowerUid === lenderUid) throw new Error("You can't spot yourself");

  // ── Eligibility: borrower must actually be broke ──
  const memberRef = doc(db, "leagues", leagueId, "members", borrowerUid);
  const memberSnap = await getDoc(memberRef);
  if (!memberSnap.exists()) throw new Error("You're not a member of this league");
  const balance = memberSnap.data().balance ?? 0;
  if (balance >= SPOT_ME_ELIGIBILITY_MAX) {
    throw new Error(`You can only request a spot when you're under ${SPOT_ME_ELIGIBILITY_MAX} pts (you have ${balance})`);
  }

  // ── No active spot-me + no active torch loan ──
  if (memberSnap.data().activeTorchLoan) {
    throw new Error("You already have an active Pass the Torch — finish that first");
  }
  if (memberSnap.data().activeSpotMe) {
    throw new Error("You already have a pending spot — wait for that one to resolve");
  }

  // ── One bailout per payday cycle (rate-limit to prevent moral hazard) ──
  // Look for any spot-me loan settled OR active in the last 7 days.
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  const recentQ = query(
    collection(db, "spotMeLoans"),
    where("leagueId", "==", leagueId),
    where("borrowerUid", "==", borrowerUid),
    where("createdAt", ">=", Timestamp.fromDate(sevenDaysAgo)),
  );
  const recentSnap = await getDocs(recentQ);
  if (recentSnap.size > 0) {
    throw new Error("You can only request one spot per week — wait for next payday");
  }

  // ── League config check ──
  const leagueRef = doc(db, "leagues", leagueId);
  const leagueSnap = await getDoc(leagueRef);
  if (!leagueSnap.exists()) throw new Error("League not found");
  const leagueData = leagueSnap.data();
  if (leagueData.spotMeEnabled === false) {
    throw new Error("Spot Me is disabled in this league");
  }

  // ── Per-pick minimum sanity check ──
  // Spot Me grants a fixed SPOT_ME_AMOUNT. If the league's per-pick minimum
  // exceeds that, the borrower would receive the spot but be unable to use
  // it on any pick (the slip drawer would reject the wager for being below
  // pickRequirements.minWager). Block the request rather than create a
  // dead-end loan.
  const spotMinWager = Number(leagueData.pickRequirements?.minWager) || 0;
  if (spotMinWager > SPOT_ME_AMOUNT) {
    throw new Error(
      `Spot Me grants ${SPOT_ME_AMOUNT} pts, but this league requires ${spotMinWager} pts/pick — ask for a regular Pass the Torch instead.`
    );
  }

  const loanData = {
    leagueId,
    borrowerUid, borrowerName,
    lenderUid, lenderName,
    amount: SPOT_ME_AMOUNT,
    borrowerBalanceAtRequest: balance, // diagnostic
    message: (message || "").slice(0, 80),
    status: "pending",
    pickId: null,        // set after lender accepts AND borrower submits a pick
    createdAt: serverTimestamp(),
    respondedAt: null,
    settledAt: null,
    lenderPayout: null,
  };

  const ref = await addDoc(collection(db, "spotMeLoans"), loanData);

  // Mark borrower's member doc so UI can show "spot pending"
  try {
    await updateDoc(memberRef, { activeSpotMe: ref.id });
  } catch (e) {
    console.warn("[SpotMe] Could not set activeSpotMe marker:", e.message);
  }

  return { id: ref.id, ...loanData };
}

// ── Respond to a Spot Me (Accept / Decline) ─────────────────────────────────
// Cloud Function path — needs admin SDK to update borrower's balance.
export async function respondToSpotMe(loanId, accept) {
  try {
    const functions = getFunctions(app);
    const fn = httpsCallable(functions, "respondToSpotMeCallable");
    const result = await fn({ loanId, accept });
    return result.data;
  } catch (cfErr) {
    console.error("[SpotMe] respondToSpotMeCallable failed:", cfErr);
    throw new Error(cfErr?.message || "Spot response failed");
  }
}

// ── Link the spot-me loan to the borrower's next pick ───────────────────────
// Called from the pick submission flow when borrower has activeSpotMe.
export async function linkSpotMePick(loanId, pickId) {
  const loanRef = doc(db, "spotMeLoans", loanId);
  await updateDoc(loanRef, { pickId });
}

// ── Load loans for display ──────────────────────────────────────────────────
export async function loadIncomingSpotMeRequests(uid, leagueId) {
  const q = query(
    collection(db, "spotMeLoans"),
    where("leagueId", "==", leagueId),
    where("lenderUid", "==", uid),
    where("status", "==", "pending"),
    orderBy("createdAt", "desc"),
    limit(10),
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function loadMyActiveSpotMe(uid, leagueId) {
  const q = query(
    collection(db, "spotMeLoans"),
    where("leagueId", "==", leagueId),
    where("borrowerUid", "==", uid),
    where("status", "in", ["pending", "active"]),
    limit(1),
  );
  const snap = await getDocs(q);
  return snap.empty ? null : { id: snap.docs[0].id, ...snap.docs[0].data() };
}
